/**
 * src/services/documentoUploadService.ts
 * =============================================================================
 * Subir un PDF al workspace autenticado del usuario. Llama al Worker
 * (api.ploter.me) en /upload-documento + PUT firmado contra el bucket. La sesión
 * se autentica vía cookie HTTP-only de Better Auth — no hace falta meter
 * tokens manualmente.
 *
 * Sin capa proyectos (eliminada en el refactor 2026-05-26): el documento
 * cuelga directamente de la organization del usuario. Si se pasa
 * `documentoId` → vN+1 sobre ese documento. Si no → documento nuevo en
 * la org activa.
 * =============================================================================
 */

import { api, ApiError } from '@/lib/api'

export interface UploadDocumentoArgs {
  file: File
  /** Si se pasa, sube como nueva versión a ESE documento. Si no, crea uno nuevo. */
  documentoId?: string
  /** Nombre legible — fallback al filename si se omite. */
  nombreDocumento?: string
  /**
   * Espacio de destino. Sin él, el documento cae en el espacio PERSONAL.
   *
   * Hay que pasarlo siempre que el usuario tenga un espacio compartido
   * seleccionado en el switcher: omitirlo era el motivo de que los espacios
   * compartidos —que se cobran a 5 €/mes— no pudieran recibir ni un archivo.
   * El backend comprueba que el usuario pertenece a ese espacio.
   */
  organizationId?: string
  onProgress?: (fraction: number) => void
}

export interface UploadDocumentoResult {
  documentoId: string
  versionId: string
  numero: number
  objectKey: string
}

export class UploadDocumentoError extends Error {
  code: string
  /**
   * Detalles que manda el servidor junto al código (p. ej. `bytesLimit` y
   * `plan` cuando la cuota está llena). Se conservan para que la UI pueda
   * decir el límite REAL del espacio: antes se perdían aquí y el modal de
   * "te has quedado sin espacio" decía siempre "300 MB", también a un Pro
   * con 50 GB o a un espacio compartido.
   */
  details?: Record<string, unknown>
  constructor(code: string, message?: string, details?: Record<string, unknown>) {
    super(message ?? code)
    this.code = code
    this.details = details
  }
}

function rebrand(e: unknown): UploadDocumentoError {
  if (e instanceof ApiError) return new UploadDocumentoError(e.code, e.message, e.details)
  if (e instanceof Error) return new UploadDocumentoError('request_failed', e.message)
  return new UploadDocumentoError('request_failed')
}

export async function uploadDocumento(args: UploadDocumentoArgs): Promise<UploadDocumentoResult> {
  const { file, documentoId, nombreDocumento, organizationId, onProgress } = args

  let r: {
    uploadUrl: string
    documentoId: string
    versionId: string
    numero: number
    objectKey: string
  }
  try {
    r = await api('/upload-documento', {
      method: 'POST',
      body: {
        documentoId,
        filename: file.name,
        size: file.size,
        contentType: file.type,
        nombreDocumento,
        organizationId,
      },
    })
  } catch (e) {
    throw rebrand(e)
  }

  if (!r?.uploadUrl || !r.documentoId || !r.versionId || !r.objectKey) {
    throw new UploadDocumentoError('invalid_response')
  }

  // PUT a R2 con progreso (XHR para tener event.lengthComputable).
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', r.uploadUrl)
    xhr.setRequestHeader('Content-Type', file.type)
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total)
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (onProgress) onProgress(1)
        resolve()
      } else reject(new UploadDocumentoError('upload_failed', `R2 ${xhr.status}`))
    }
    xhr.onerror = () => reject(new UploadDocumentoError('network_error'))
    xhr.send(file)
  })

  // Confirmar la subida. NO es opcional ni "nice to have": hasta que este
  // paso ocurre, la versión está sin confirmar y por tanto INVISIBLE en la
  // app, y el cron la barrerá a las 6 horas.
  //
  // Es el paso que impide los documentos fantasma: antes la fila se daba por
  // buena al pedir la URL, así que una subida fallida dejaba un documento que
  // aparecía en la lista y al abrirlo no había nada. Ahora el servidor
  // verifica contra R2 que el archivo está de verdad, y de paso corrige el
  // tamaño con el real (el declarado por el navegador ya no es la verdad
  // contable). Ver `cloudflare/src/routes/confirm-upload.ts`.
  try {
    await api('/confirm-upload', { method: 'POST', body: { versionId: r.versionId } })
  } catch (e) {
    throw rebrand(e)
  }

  return { documentoId: r.documentoId, versionId: r.versionId, numero: r.numero ?? 1, objectKey: r.objectKey }
}

export interface DocumentoVersionItem {
  id: string
  versionNumber: number
  createdAt: string
  sizeBytes: number
}

/**
 * Rol del user dentro del documento:
 *  - admin / collaborator: miembro de la org dueña — puede todo
 *  - guest: invitado nominal — solo ver + anotar
 */
export type DocumentoAccessRole = 'admin' | 'collaborator' | 'guest'

export interface ViewDocumentoResult {
  /** URL firmada GET de R2 (10 min de vida). */
  url: string
  /** ID de la versión devuelta (la pedida explícitamente o la current_version). */
  versionId: string
  /** Número de la versión (1, 2, 3...) para mostrarlo en el visor. */
  numero: number
  /** Nombre del documento (sin la extensión "real" .pdf — puede o no
   *  estar en el campo). Lo usa el cliente para nombrar la descarga
   *  con anotaciones quemadas client-side. */
  nombre: string
  /** Lista completa de versiones del documento, en orden ascendente. */
  versions: DocumentoVersionItem[]
  /** Rol del user actual. Frontend lo usa para esconder UI que backend
   *  rechazaría (ej. el botón Invitar a un guest). */
  role: DocumentoAccessRole
  /** ISO de caducidad del documento, o null si no caduca. Alimenta el aviso
   *  del tier free en el visor. */
  expiresAt: string | null
}

/**
 * Devuelve info para ABRIR el documento en el visor. Si no se pasa
 * `versionId`, el Worker devuelve la versión "actual" del documento
 * (current_version_id). Para abrir una versión histórica concreta,
 * pasa su id.
 */
export async function viewDocumento(
  documentoId: string,
  versionId?: string,
): Promise<ViewDocumentoResult> {
  try {
    const r = await api<{
      url: string
      versionId: string
      numero: number
      nombre?: string
      versions?: DocumentoVersionItem[]
      role?: DocumentoAccessRole
      expiresAt?: string | null
    }>('/view-documento', {
      method: 'POST',
      body: { documentoId, versionId },
    })
    if (!r?.url || !r.versionId) throw new UploadDocumentoError('invalid_response')
    return {
      url: r.url,
      versionId: r.versionId,
      numero: r.numero ?? 1,
      // Fallback si el backend no envió `nombre` (versión antes del cambio
      // de esquema; hoy siempre llega). Solo se ve en el nombre del archivo
      // descargado. Un servicio no tiene `t()`, así que en vez de una palabra
      // en español se usa algo neutro en cualquier idioma: la marca y el
      // número de versión ("ploter-v3").
      nombre: r.nombre ?? `ploter-v${r.numero ?? 1}`,
      versions: r.versions ?? [],
      // Default a 'guest' por seguridad — si el backend no devolvió role
      // (versión vieja), asumimos lo más restrictivo. UI esconderá Invitar.
      role: r.role ?? 'guest',
      // Sin dato del backend asumimos que NO caduca: el aviso simplemente no
      // se pinta. Prometer una caducidad que no sabemos es peor que callar.
      expiresAt: r.expiresAt ?? null,
    }
  } catch (e) {
    if (e instanceof UploadDocumentoError) throw e
    throw rebrand(e)
  }
}

/**
 * @deprecated usar `viewDocumento` que devuelve también versionId + numero.
 * Mantenido temporalmente por compatibilidad mientras migramos callers.
 */
export async function getDocumentoViewUrl(documentoId: string, versionId?: string): Promise<string> {
  const r = await viewDocumento(documentoId, versionId)
  return r.url
}
